import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import { Flex, Table, Tooltip } from "antd";
import {
  LoadingOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined,
} from "@ant-design/icons";
import { isMobile, isSafari } from "react-device-detect";

import StatusTag from "../../../Common/components/StatusTag";

import ReportType from "./ReportType";
import "./SendReportsTable.css";

const { Column } = Table;

export default function SendReportsTable({
  data,
  tab,
  loading = false,
  sortCreatedBy,
  order,
  user,
  counter,
  onPreview,
  selectedRowKeys,
  onCheckboxChange,
  onSelectChange,
}) {
  const [fileLoadingIndex, setFileLoadingIndex] = useState(null);
  const isMounted = useRef(false);
  const tableBodyHeight = useRef(300); // Default height
  const containerRef = useRef(null);
  const contentWidthRef = useRef(1000);
  // const tableOffsetRef = useRef(120);
  const [headerOffset, setHeaderOffset] = useState(120);

  /**
   * Handle clicking in File cell
   * @param {*} report report
   * @param {number} index index
   */
  const onFileCellClick = async (report, index) => {
    setFileLoadingIndex(index);
    try {
      await onPreview(report, index);
    } finally {
      // Only clear if the same row is still active
      setFileLoadingIndex((currentIndex) => (currentIndex === index ? null : currentIndex));
    }
  };

  /**
   * handle clicking cancel icon in select all checkbox cell
   */
  const handleClearAllChecks = () => {
    onCheckboxChange([]);
  };

  const dataSource = useMemo(() => data, [data]);

  useEffect(() => {
    const resizeHandler = () => {
      // get table height using the container height
      const CONTAINER_CLASSNAME = ".customTabSendReport";
      const OFFSET = 130; // height of table header + height of filters row
      const container = document.querySelector(CONTAINER_CLASSNAME);
      const containerSize = container.getBoundingClientRect();
      const containerHeight = containerSize?.height;
      const tableHeight = containerHeight - OFFSET;
      tableBodyHeight.current = tableHeight;

      // get container width from window width
      const SIDE_BAR_CLASSNAME = ".sideBarMenuStyle";
      const CONTENT_CONTAINER_CLASSNAME = ".LoggedIn__Container__Content__Switch";
      const sideBarWidth = document.querySelector(SIDE_BAR_CLASSNAME).getBoundingClientRect()?.width || 210;
      const contentContainerPaddingX = parseFloat(window.getComputedStyle(document.querySelector(CONTENT_CONTAINER_CLASSNAME))?.paddingLeft) || 40;
      const contentPaddingX = parseFloat(window.getComputedStyle(container)?.paddingLeft) || 43;
      const contentWidth = window.innerWidth - sideBarWidth - (contentContainerPaddingX * 2) - (contentPaddingX * 2);
      containerRef.current.style.width = `${contentWidth}px`;
      containerRef.current.style.maxWidth = `${contentWidth}px`;
      contentWidthRef.current = contentWidth;

      // get table header for adjusting table offset height
      const TABLE_HEADER_CLASSNAME = ".SendReportsServer__Header";
      // const tableHeader = document.querySelector(TABLE_HEADER_CLASSNAME);
      // const tableHeaderHeight = tableHeader.getBoundingClientRect()?.height;
      // tableOffsetRef.current = tableHeaderHeight;
      const tableHeader = document.querySelector(TABLE_HEADER_CLASSNAME);
      if (tableHeader) {
        const tableHeaderHeight = tableHeader.getBoundingClientRect()?.height;
        setHeaderOffset(tableHeaderHeight || 120);
      }
    };

    if (!isMounted.current) {
      resizeHandler();
      isMounted.current = true;
    }

    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  const containerStyles = {
    width: "100%",
    maxWidth: 1000,
  };

  return (
    <div
      ref={containerRef}
      style={containerStyles}
    >
      <Table
        dataSource={dataSource}
        rowKey={(file) => `${file.wo}-${file.reportID}`}
        rootClassName={`SendReportTable ${isMobile || isSafari ? " SafariTableDisplay" : ""}`}
        rowClassName="SendReportTable__Row"
        bordered
        loading={loading}
        pagination={false}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: onCheckboxChange,
          onSelect: (record) => onSelectChange(record),
          columnWidth: 80,
          columnTitle: (node) => (
            <div className="SendReportTable__SelectAllCheckbox">
              {node}
              <span className="SendReportTable__Counter">{counter}</span>
              <span onClick={handleClearAllChecks}>
                <i className="fa fa-times sendReportsCounterIcon" />
              </span>
            </div>
          ),
        }}
        sticky={{
          offsetHeader: headerOffset,
        }}
        onRow={(record) => ({
          onClick: () => {
            // Remove the selectedRowKeys manipulation here
            // Just call onSelectChange to handle the selection
            onSelectChange(record);
          },
        })}
        virtual
        scroll={{
          x: Math.min(containerStyles.maxWidth, contentWidthRef.current),
          y: tableBodyHeight.current,
        }}
        tableLayout="fixed"
      >

        <Column
          title="Work Order"
          dataIndex="wo"
          key="wo"
          width={120}
        />

        <Column
          title="Customer Name"
          dataIndex="customer"
          key="customer"
          width={250}
        />

        <Column
          title="File"
          key="path"
          className="sendReportsFilePath"
          onCell={(report, index) => ({
            onClick: () => {
              // e.stopPropagation(); // Prevent row click
              onFileCellClick(report, index);
            },
          })}
          render={(file, _, index) => (
            <div className="SendReportAuto__FileName">
              <div className="SendReportAuto__FileName__Left">
                <p>{file.path}</p>
                {fileLoadingIndex === index && <LoadingOutlined />}
              </div>
              <ReportType result={file.report_type} />
            </div>
          )}
          width={350}
        />

        <Column
          title={(
            <Flex justify="flex-start" align="center" gap={8}>
              <span>QC Status</span>
              <Tooltip
                className="SendReportsTable__Tooltip"
                overlayClassName="SendReportsTable__TooltipOverlay"
                placement="top"
                title={(
                  <div>
                    <div className="SendReportsTable__TooltipTitle">
                      Quality Control Status
                    </div>
                    <div className="SendReportsTable__TooltipContent">
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusPending">
                          Pending
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - Not yet QCed
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusPassed">
                          Passed
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - QC checks passed
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusFailed">
                          Failed
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - QC checks failed
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusNoHistory">
                          No History
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - No QC history available
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              >
                <InfoCircleOutlined className="SendReportsTable__InfoIcon" />
              </Tooltip>
            </Flex>
          )}
          key="qc"
          render={(file) => <StatusTag type="QC" result={file.qc} />}
          width={120}
        />

        <Column
          title={(
            <Flex justify="flex-start" align="center" gap={8}>
              <span>Report Status</span>
              <Tooltip
                className="SendReportsTable__Tooltip"
                overlayClassName="SendReportsTable__TooltipOverlay"
                placement="top"
                title={(
                  <div>
                    <div className="SendReportsTable__TooltipTitle">
                      Report Approval Status
                    </div>
                    <div className="SendReportsTable__TooltipContent">
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusPending">
                          Pending
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - Awaiting approval
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusApproved">
                          Approved
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - Report approved
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusHold">
                          Hold
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - Report on hold
                        </span>
                      </div>
                      <div className="SendReportsTable__TooltipItem">
                        <span className="SendReportsTable__TooltipBullet">•</span>
                        <span className="SendReportsTable__TooltipStatus SendReportsTable__StatusRetesting">
                          Retesting
                        </span>
                        <span className="SendReportsTable__TooltipDescription">
                          - Report requires retesting
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              >
                <InfoCircleOutlined className="SendReportsTable__InfoIcon" />
              </Tooltip>
            </Flex>
          )}
          key="approve_status"
          render={(file) => (
            <div className="SendReportAuto__ReportStatus">
              <Tooltip
                placement="top"
                title={file.approved_by && user.permissionMap.view_sendreports_approved_by ? (
                  <div>
                    <span>{file.approved_by}</span>
                    <br />
                    <span className="SendReportsServer__Time">{file.approved_at}</span>
                  </div>
                ) : ""}
              >
                <span><StatusTag result={file.approve_status} type="REPORT" /></span>
              </Tooltip>
            </div>
          )}
          width={140}
        />

        {
          user.permissionMap.view_sendreports_created_by && (
            <Column
              title={(
                <Flex justify="flex-start" align="center" gap={8} className="SendReportsTable__CursorPointer">
                  <span>Created By</span>
                  {order === "ASC" ? (
                    <ArrowUpOutlined className="SendReportsTable__InfoIcon" />
                  ) : (
                    <ArrowDownOutlined className="SendReportsTable__InfoIcon" />
                  )}
                </Flex>
              )}
              key="created_by"
              onHeaderCell={() => ({
                onClick: () => tab !== "pending" && tab !== "hold" && sortCreatedBy(),
              })}
              render={(file) => (
                <Tooltip placement="top" title={file.time}>
                  <span>{file.created_by}</span>
                </Tooltip>
              )}
              width={180}
            />
          )
        }

        {
          tab === "history" && (
            <Column
              title="Sent By"
              key="sent_by"
              render={(file) => (
                <Tooltip placement="top" title={file.sent_at}>
                  <span>{file.sent_by}</span>
                </Tooltip>
              )}
              width={180}
            />
          )
        }

      </Table>
    </div>
  );
}
